import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import GoogleTranslate from './GoogleTranslate';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDiscoverOpen, setIsDiscoverOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: 'Accueil', path: '/' },
    { name: 'Album photos', path: '/photos' },
    { name: 'Réservation', path: '/reservation' },
    { name: 'Contact', path: '/contact' },
    { name: 'Livre d\'or', path: '/livre-dor' }
  ];

  const discoverItems = [
    { name: 'Île de Ré', path: '/ile-de-re' },
    { name: 'Les Portes-en-Ré', path: '/les-portes-en-re' },
    { name: 'Plages de l\'île', path: '/plages-ile-de-re' },
    { name: 'Activités', path: '/activites-ile-de-re' },
    { name: 'Que faire ?', path: '/que-faire-ile-de-re' },
    { name: 'Week-end', path: '/weekend-ile-de-re' },
    { name: 'Moyenne saison', path: '/moyenne-saison-ile-de-re' },
    { name: 'Séminaires', path: '/seminaire-ile-de-re' },
    { name: 'Vacances Famille', path: '/vacances-famille-ile-de-re' },
    { name: 'Golf', path: '/golf-ile-de-re' },
    { name: 'Villa Luxe', path: '/location-villa-luxe-ile-de-re' },
  ];

  const isActive = (path) => location.pathname === path;
  const isDiscoverActive = () => discoverItems.some(item => location.pathname === item.path);

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center border-2 border-amber-700 group-hover:scale-110 transition-transform duration-300 shadow-md">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 22V12H15V22" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <div className="text-xl font-serif text-stone-800 leading-tight">Villa NOMADE</div>
              <div className="text-xs text-amber-700">Les Portes en Ré</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path}>
                <Button
                  variant="ghost"
                  className={`text-base transition-all duration-300 ${
                    isActive(item.path)
                      ? 'text-amber-700 font-semibold bg-amber-50'
                      : 'text-stone-700 hover:text-amber-700 hover:bg-amber-50'
                  }`}
                >
                  {item.name}
                </Button>
              </Link>
            ))}
            
            {/* Dropdown Découvrir */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className={`text-base transition-all duration-300 ${
                    isDiscoverActive()
                      ? 'text-amber-700 font-semibold bg-amber-50'
                      : 'text-stone-700 hover:text-amber-700 hover:bg-amber-50'
                  }`}
                >
                  Découvrir
                  <ChevronDown className="ml-1 w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {discoverItems.map((item) => (
                  <DropdownMenuItem key={item.path} asChild>
                    <Link 
                      to={item.path} 
                      className={`cursor-pointer ${
                        isActive(item.path) ? 'bg-amber-50 text-amber-700 font-semibold' : ''
                      }`}
                    >
                      {item.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Google Translate Widget */}
            <div className="ml-4 pl-4 border-l border-stone-200">
              <GoogleTranslate />
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-stone-700 hover:text-amber-700 transition-colors"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden pb-4">
            <div className="flex flex-col gap-2">
              {navItems.map((item) => (
                <Link key={item.path} to={item.path} onClick={() => setIsOpen(false)}>
                  <Button
                    variant="ghost"
                    className={`w-full justify-start text-base transition-all duration-300 ${
                      isActive(item.path)
                        ? 'text-amber-700 font-semibold bg-amber-50'
                        : 'text-stone-700 hover:text-amber-700 hover:bg-amber-50'
                    }`}
                  >
                    {item.name}
                  </Button>
                </Link>
              ))}
              
              {/* Mobile Découvrir */}
              <div className="border-t border-stone-200 pt-2 mt-2">
                <button
                  onClick={() => setIsDiscoverOpen(!isDiscoverOpen)}
                  className="w-full flex items-center justify-between px-4 py-2 text-stone-700 hover:text-amber-700 hover:bg-amber-50 rounded-md transition-colors"
                >
                  <span className="font-medium">Découvrir l'île</span>
                  <ChevronDown className={`w-5 h-5 transition-transform ${isDiscoverOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isDiscoverOpen && (
                  <div className="pl-4 mt-2 space-y-1">
                    {discoverItems.map((item) => (
                      <Link key={item.path} to={item.path} onClick={() => setIsOpen(false)}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`w-full justify-start text-sm ${
                            isActive(item.path)
                              ? 'text-amber-700 font-semibold bg-amber-50'
                              : 'text-stone-600 hover:text-amber-700 hover:bg-amber-50'
                          }`}
                        >
                          {item.name}
                        </Button>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Google Translate for Mobile */}
              <div className="mt-4 px-4 pt-4 border-t border-stone-200">
                <GoogleTranslate />
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;